import {
	checkImportStatusForSessionAsync,
	createUploadSessionAsync,
	ImportStatus,
	IUploadResult,
	IUploadSessionResponse,
	upload,
} from "../../services/upload-service";
import { batchTasks } from "../../utils/batchTasks";
import { getCookie } from "../../utils/cookie";

export interface IUploadOptions {
	onBeforeUploadStart?: () => void;
	onFileUploaded?: (amountUploadedFiles: number) => void;
	onUploadFinished?: (
		folderId: string,
		importedFileIds: string[] | null,
		failedUploadAmount: number,
		failedFileNames: string[] | null,
	) => void;
	flow?: string;
	uploadTechnology?: string;
	batchSize?: number;
}

export interface IUpload {
	readonly isUploading: () => boolean;
}

export async function createUpload(files: FileList, folderId: string, opts?: IUploadOptions): Promise<IUpload> {
	let failedFileNames: string[] = [];
	let uploadErrors = 0;
	let pollSessionCounter = 0;
	let uploading = false;
	let polling = false;
	const accountId = getCookie("accid");

	const {
		onBeforeUploadStart,
		onFileUploaded,
		onUploadFinished,
		flow = "myalbums",
		uploadTechnology = "html5",
		batchSize = 5,
	} = opts;

	const init = async (): Promise<void> => {
		uploading = true;
		const uploadSession = await createUploadSessionAsync(folderId, flow, uploadTechnology, accountId);
		if (uploadSession) createUploadSessionComplete(uploadSession);
	};

	const createUploadSessionComplete = (session: IUploadSessionResponse) => {
		if (onBeforeUploadStart) onBeforeUploadStart();
		executeUploadsInBatch(session, Array.from(files), batchSize);
	};

	const executeUploadsInBatch = async (session: IUploadSessionResponse, files: File[], batchSize: number) => {
		let fileCount = 1;

		const onBachUploaded = (filesFromBatch: IUploadResult[]) => {
			filesFromBatch.forEach((res) => {
				if (!res.success) {
					uploadErrors++;
					failedFileNames.push(res.file.name);
				}

				if (fileCount === files.length) {
					if (uploadErrors === files.length) {
						onUploadFinished && onUploadFinished(folderId, null, uploadErrors, failedFileNames);
						uploading = false;
					}
				}
				fileCount++;
			});
			if (polling === false) startSessionPolling(files.length - uploadErrors, session.id);
		};

		const tasks = files.map((file) => () => uploadFile(session, file));

		for await (const batch of batchTasks(tasks, batchSize)) {
			onBachUploaded(batch as IUploadResult[]);
		}
	};

	const startSessionPolling = (expectedFiles: number, sessionId: string) => {
		polling = true;
		pollSessionCounter = 0;
		window.setTimeout(() => pollSession(expectedFiles, sessionId), 1000);
	};

	const pollSession = (expectedFiles: number, sessionId: string) => {
		pollSessionCounter++;

		checkImportStatusForSessionAsync(sessionId, accountId).then((res) => {
			const finishedImports = res.filter((sessionStatus) => sessionStatus.status !== ImportStatus.Waiting);
			const importedFiles = res.filter((importItem) => importItem.status === ImportStatus.Imported);
			const isLoggedOut = res.some((item) => item.status === ImportStatus.LoggedOut);
			if (onFileUploaded) onFileUploaded(importedFiles.length);

			if (finishedImports.length === expectedFiles || isLoggedOut) {
				const failedFiles = res.filter((i) => i.status === ImportStatus.Failed);
				failedFileNames.push(...failedFiles.map((m) => m.originalFileName));
				uploadErrors += failedFiles.length;
				onUploadFinished &&
					onUploadFinished(
						folderId,
						importedFiles.map((importedFile) => importedFile.fileId),
						uploadErrors,
						failedFileNames,
					);
				resetUploadErrors();
				uploading = false;
				polling = false;
			} else {
				window.setTimeout(() => pollSession(expectedFiles, sessionId), 1000 + pollSessionCounter * 1000);
			}
		});
	};

	const resetUploadErrors = () => {
		failedFileNames = [];
		uploadErrors = 0;
	};

	const uploadFile = async (session: IUploadSessionResponse, file: File): Promise<IUploadResult> => {
		return upload(session.uploadUrl + "?uploadId=" + session.id, file);
	};

	const isUploading = () => {
		return uploading;
	};

	await init();

	return {
		isUploading,
	} as IUpload;
}
